import { Building2, CalendarClock, Car, ChevronRight, FileEdit, Inbox } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge } from '@/ui/StatusBadge';

import {
  apresentacaoDoStatus,
  CAMINHO_DO_AUTOCADASTRO,
  caminhoDoVeiculo,
  formatarDataHora,
  formatarPlaca,
  ROTULO_DO_TIPO_DE_ENTIDADE,
  rotuloDaDre,
  rotuloDaModalidade,
  type EntidadeInscrita,
  type VeiculoInscrito,
} from './AcompanhamentoContrato';
import { useMinhasInscricoes } from './AcompanhamentoDados';
import { BotaoSair, CartaoParaEntrar } from './AcompanhamentoSessao';
import { useSessaoDoInscrito } from './identidade/api';

/**
 * Minhas inscrições — `/credenciados/acompanhamento` (F06, RN-CP-30).
 *
 * EXIGE A SESSÃO DO INSCRITO (F04). Sem ela, a tela manda entrar e não mostra
 * nada. Com ela, lista as entidades que a conta inscreveu — uma por
 * submissão (PF, PJ ou cooperativa, D-13) —, e dentro de cada uma os veículos,
 * cada um com protocolo, status, próximo passo e prazo (D-14).
 *
 * O CARTÃO DO VEÍCULO INTEIRO É O LINK para o detalhe (regra 9 de interface):
 * um `<Link>` de bloco, alcançável por teclado, sem botão "Ver" ao lado.
 */

export function AcompanhamentoPage() {
  const sessao = useSessaoDoInscrito();
  const temSessao = Boolean(sessao.data);
  const consulta = useMinhasInscricoes(temSessao);
  const navegar = useNavigate();

  useEffect(() => {
    document.title = 'Minhas inscrições — Sistema TEG';
  }, []);

  if (sessao.isPending) {
    return (
      <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <Spinner rotulo="Conferindo sua sessão…" />
      </section>
    );
  }

  if (sessao.isError) {
    return (
      <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <ErrorState
          variant="load-failed"
          title="Não foi possível conferir sua sessão"
          description="Confira sua conexão e tente de novo em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void sessao.refetch() }}
        />
      </section>
    );
  }

  if (!sessao.data || consulta.error?.situacao === 401) {
    return <CartaoParaEntrar sessaoTerminou={consulta.error?.situacao === 401} />;
  }

  const entidades = consulta.data?.entidades ?? [];

  return (
    <section
      className="mx-auto w-full max-w-3xl space-y-5 px-4 py-8 sm:px-6 sm:py-10"
      aria-labelledby="minhas-inscricoes-titulo"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h1 id="minhas-inscricoes-titulo" className="text-xl font-extrabold sm:text-2xl">
            Minhas inscrições
          </h1>
          <p className="text-muted-foreground text-sm">
            {sessao.data.inscrito.nome ?? 'Inscrito'} · CPF{' '}
            <span className="font-mono">{sessao.data.inscrito.cpfMascarado}</span>
          </p>
        </div>
        <BotaoSair />
      </header>

      {consulta.isPending && <Spinner rotulo="Carregando suas inscrições…" />}

      {consulta.isError && (
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar suas inscrições"
          description="Confira sua conexão e tente de novo em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
        />
      )}

      {consulta.isSuccess && entidades.length === 0 && (
        <Card className="rounded-2xl">
          <CardContent className="p-0">
            <EmptyState
              icon={Inbox}
              title="Você ainda não tem inscrição"
              description="Quando você enviar a inscrição de um veículo, o andamento dela aparece aqui."
              actionLabel="Fazer minha inscrição"
              onAction={() => navegar(CAMINHO_DO_AUTOCADASTRO)}
            />
          </CardContent>
        </Card>
      )}

      {consulta.isSuccess && entidades.length > 0 && (
        <ul className="space-y-4" aria-label="Entidades inscritas">
          {entidades.map((entidade) => (
            <li key={entidade.submissaoId}>
              <CartaoDaEntidade entidade={entidade} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CartaoDaEntidade({ entidade }: { entidade: EntidadeInscrita }) {
  const rascunho = entidade.situacao === 'RASCUNHO';
  return (
    <Card className="rounded-2xl">
      <CardHeader className="space-y-1 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Building2 className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
          <CardTitle className="min-w-0 text-base wrap-break-word">
            {entidade.entidade.nome}
          </CardTitle>
          <Badge variant="outline" className="text-[11px]">
            {ROTULO_DO_TIPO_DE_ENTIDADE[entidade.entidade.tipo]}
          </Badge>
        </div>
        <p className="text-muted-foreground text-xs">
          <span className="font-mono">{entidade.entidade.documentoMascarado}</span> ·{' '}
          {entidade.ciclo.nome}
          {entidade.enviadaEm && <> · enviada em {formatarDataHora(entidade.enviadaEm)}</>}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {rascunho ? (
          <div className="border-border bg-muted/40 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed p-3">
            <p className="text-sm">
              Inscrição em preenchimento — ainda não foi enviada, e os veículos não têm protocolo.
            </p>
            <Button asChild size="sm" className="h-9">
              <Link to={CAMINHO_DO_AUTOCADASTRO}>
                <FileEdit className="h-4 w-4" aria-hidden />
                Continuar inscrição
              </Link>
            </Button>
          </div>
        ) : (
          <ul className="space-y-2" aria-label={`Veículos de ${entidade.entidade.nome}`}>
            {entidade.veiculos.map((veiculo) => (
              <li key={veiculo.protocolo}>
                <CartaoDoVeiculo veiculo={veiculo} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function CartaoDoVeiculo({ veiculo }: { veiculo: VeiculoInscrito }) {
  const apresentacao = apresentacaoDoStatus(veiculo.status);
  const pedeAcao = veiculo.proximoPasso.codigo === 'CORRIGIR';
  return (
    <Link
      to={caminhoDoVeiculo(veiculo.protocolo)}
      className={cn(
        'border-border bg-card hover:border-primary/40 hover:bg-muted/40 focus-visible:ring-ring block space-y-2 rounded-xl border p-3 transition-colors focus:outline-hidden focus-visible:ring-2',
        pedeAcao && 'border-warning/50',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Car className="h-4 w-4 shrink-0" aria-hidden />
            {formatarPlaca(veiculo.placa)}
            <span className="text-muted-foreground font-normal">
              · {rotuloDaModalidade(veiculo.modalidade)}
            </span>
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs wrap-break-word">
            Protocolo <span className="font-mono">{veiculo.protocolo}</span>
            {veiculo.dre && <> · {rotuloDaDre(veiculo.dre.nome)}</>}
          </p>
        </div>
        <StatusBadge
          status={apresentacao.variante}
          label={apresentacao.rotulo}
          className="shrink-0"
        />
      </div>
      <p className="text-sm">{veiculo.proximoPasso.texto}</p>
      <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-2 text-xs">
        {veiculo.prazo ? (
          <span
            className={cn('flex items-center gap-1', pedeAcao && 'text-foreground font-medium')}
          >
            <CalendarClock className={cn('h-3.5 w-3.5', pedeAcao && 'text-warning')} aria-hidden />
            Prazo: {formatarDataHora(veiculo.prazo)}
          </span>
        ) : (
          <span>Atualizada em {formatarDataHora(veiculo.atualizadoEm)}</span>
        )}
        <span className="text-primary flex items-center gap-0.5 font-medium">
          Ver andamento
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </span>
      </div>
    </Link>
  );
}

export default AcompanhamentoPage;
